import { type CSSProperties, Fragment, type ReactNode, type Ref } from 'react'
import cx from 'classnames'
import { type ButtonRenderProps, RawButton } from '@/components/Button'
import { match } from '@/utils/pattern-match-util'
import {
  CheckboxContext,
  GridList,
  GridListContext,
  GridListItem,
  type GridListItemProps,
  type GridListProps,
  type Key,
  type Selection,
  useContextProps,
  useSlottedContext,
} from 'react-aria-components'
import { type To, useHref, useResolvedPath } from 'react-router'
import type { PressEventHandler } from '@/infra/prop-types'
import styles from './Card.module.css'
import {
  AutoSkeleton,
  type SkeletonColor,
  useIsSkeleton,
} from '@/components/Skeleton'
import {
  Icon,
  type IconName,
  type IconSkeletonVariant,
} from '@/components/Icon'
import flattenChildren from 'react-keyed-flatten-children'
import { Checkbox } from '@/components/inputs/Checkbox'
import { Radio } from '@/components/inputs/Radio'
import { type LinkRenderProps, RawLink } from '@/components/Link'

export interface CardProps {
  children?: ReactNode
  isActive?: boolean
  isSkeleton?: boolean
  skeletonColor?: SkeletonColor
  testId?: string
}

/**
 * Card root.
 */
export function Card({
  children,
  isActive,
  isSkeleton,
  skeletonColor,
  testId,
}: CardProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <div
      data-test={testId}
      className={cx(
        styles.root,
        isActive && styles.active,
        getSkeletonClassName(isSkeleton, skeletonColor),
      )}
    >
      {children}
    </div>
  )
}

interface CardButtonProps {
  children?: ReactNode | ((props: ButtonRenderProps) => ReactNode)
  onPress?: PressEventHandler
  isDisabled?: boolean
  'aria-label'?: string
  isActive?: boolean
  isSkeleton?: boolean
  skeletonColor?: SkeletonColor
  testId?: string
}

/**
 * A button, styled like a card.
 */
export function CardButton({
  children,
  isActive,
  isSkeleton,
  skeletonColor,
  testId,
  ...rest
}: CardButtonProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <RawButton
      testId={testId}
      className={({ isFocusVisible, isDisabled, isHovered, isPressed }) =>
        cx(
          styles.item,
          isFocusVisible && styles.focusVisible,
          isHovered && styles.hover,
          isPressed && styles.pressed,
          isDisabled && styles.disabled,
          isActive && styles.active,
          getSkeletonClassName(isSkeleton, skeletonColor),
        )
      }
      {...rest}
    >
      {children}
    </RawButton>
  )
}

interface CardListProps<T extends object = object> {
  children?: GridListProps<T>['children']
  'aria-label'?: string
  'aria-labelledby'?: string
  renderEmptyState?: () => ReactNode
  items?: T[]
  selectedKeys?: Selection
  onSelectionChange?: (selection: Selection) => void
  disallowEmptySelection?: boolean
  selectionMode?: GridListProps<T>['selectionMode']
  selectionBehavior?: GridListProps<T>['selectionBehavior']
  escapeKeyBehavior?: GridListProps<T>['escapeKeyBehavior']
  layout?: 'grid' | 'stack'
  gridColumns?: 2 | 3 | 4
  spacing?: 'sm' | 'base'
}

/**
 * Card list with interactive items.
 */
export function CardList<T extends object = object>({
  children,
  renderEmptyState,
  selectionMode,
  gridColumns = 2,
  spacing = 'sm',
  ...rest
}: CardListProps<T>) {
  return (
    <GridListContext value={{ selectionMode }}>
      <GridList<T>
        {...rest}
        selectionMode={selectionMode}
        className={cx(
          styles.list,
          rest.layout === 'grid' && styles.grid,
          spacing === 'base' && styles.spacingBase,
        )}
        style={
          {
            '--grid-cols': rest.layout === 'grid' ? gridColumns : undefined,
          } as CSSProperties
        }
        renderEmptyState={
          renderEmptyState ? () => renderEmptyState() : undefined
        }
      >
        {children}
      </GridList>
    </GridListContext>
  )
}

interface StaticCardListProps {
  children?: ReactNode
  'aria-label'?: string
  'aria-labelledby'?: string
  layout?: 'grid' | 'stack'
  gridColumns?: 2 | 3 | 4
  spacing?: 'sm' | 'base'
}

/**
 * Card list with static items.
 */
export function StaticCardList({
  children,
  gridColumns = 2,
  spacing = 'sm',
  ...rest
}: StaticCardListProps) {
  return (
    <ul
      {...rest}
      className={cx(
        styles.list,
        rest.layout === 'grid' && styles.grid,
        spacing === 'base' && styles.spacingBase,
      )}
      style={
        {
          '--grid-cols': rest.layout === 'grid' ? gridColumns : undefined,
        } as CSSProperties
      }
    >
      {children}
    </ul>
  )
}

interface CardListItemProps<T extends object = object> {
  children?: GridListItemProps<T>['children']
  isDisabled?: boolean
  onAction?: () => void
  to?: To
  replace?: boolean
  textValue?: string
  id?: Key
  ref?: Ref<HTMLDivElement>
  isActive?: boolean
  isSkeleton?: boolean
  skeletonColor?: SkeletonColor
  testId?: string
}

/**
 * Card list item for lists of interactive items.
 */
export function CardListItem({
  children,
  isDisabled,
  replace,
  onAction,
  to,
  id,
  textValue,
  ref,
  isActive,
  isSkeleton,
  skeletonColor,
  testId,
}: CardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const [{ selectionMode }] = useContextProps(
    {} as GridListProps<unknown>,
    null,
    GridListContext,
  )

  const resolved = useHref(useResolvedPath(to ?? '/'))
  const isInteractive = Boolean(to ?? onAction ?? selectionMode)
  return (
    <GridListItem
      id={id}
      ref={ref}
      isDisabled={isDisabled || isSkeleton}
      onAction={onAction}
      href={to ? resolved : undefined}
      textValue={textValue}
      data-test={testId}
      // @ts-ignore: React Router options
      routerOptions={{ replace }}
      className={({
        isFocusVisible,
        isSelected,
        isDisabled,
        isHovered,
        isPressed,
      }) =>
        cx(
          styles.item,
          isFocusVisible && styles.focusVisible,
          isPressed && styles.pressed,
          (isDisabled || !isInteractive) && styles.disabled,
          isHovered && styles.hover,
          isSelected && styles.selected,
          isActive && styles.active,
          getSkeletonClassName(isSkeleton, skeletonColor),
        )
      }
    >
      {children}
    </GridListItem>
  )
}

interface StaticCardListItemProps {
  children?: ReactNode
  isSkeleton?: boolean
  skeletonColor?: SkeletonColor
  testId?: string
}

/**
 * Card list item for lists of static items.
 */
export function StaticCardListItem({
  children,
  isSkeleton,
  skeletonColor,
  testId,
}: StaticCardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <li
      data-test={testId}
      className={cx(
        styles.static,
        getSkeletonClassName(isSkeleton, skeletonColor),
      )}
    >
      {children}
    </li>
  )
}

interface CardItemIconProps {
  name: IconName
  variant?: 'default' | 'secondary' | 'surface-background'
  skeleton?: IconSkeletonVariant
  rotate?: number
}

/**
 * Icon container for an icon rendered in a card item, such
 * as a {@link CardListItem} or a {@link CardButton}.
 */
export function CardItemIcon({
  name,
  variant = 'default',
  skeleton,
  rotate,
}: CardItemIconProps) {
  return (
    <div
      className={cx(
        styles.itemIcon,
        variant === 'secondary' && styles.secondary,
        variant === 'surface-background' && styles.surfaceBackground,
      )}
    >
      <Icon
        name={name}
        skeleton={skeleton}
        variant={
          variant === 'surface-background' ? 'surface-background' : 'inherit'
        }
        rotate={rotate}
      />
    </div>
  )
}

interface CardContentProps {
  children?: ReactNode
  testId?: string
}

/**
 * Content for a {@link Card}.
 */
export function CardContent({ children, testId }: CardContentProps) {
  return (
    <div data-test={testId} className={cx(styles.content)}>
      {children}
    </div>
  )
}

interface CardItemContentProps {
  children?: ReactNode

  /**
   * Items to render to the right of the text but before
   * the chevron (if interactive)
   */
  extras?: ReactNode

  /**
   * Icon to render to the left of the content.
   */
  icon?: ReactNode | null | false

  /**
   * Decoration to render to the left of the content.
   * The root must be a {@see CardItemContentDecoration}.
   */
  decoration?: ReactNode | null | false

  /**
   * When `true`, a chevron icon will be rendered at the end
   * of the item to indicate that the item itself is interactive.
   */
  isInteractive?: boolean

  /**
   * Layout to use.
   * The stacked layout will put the text underneath the decorations
   * and extras.
   */
  layout?: 'default' | 'stacked'

  /**
   * What spacing to use on the yaxis.
   *
   * @default 'lg'
   */
  spacingY?: 'lg' | 'base'

  /**
   * What spacing to use on the right side.
   *
   * When using a {@link Button} in the {@link extras}, a value of `sm`
   * is preferred for optimal spacing.
   *
   * @default 'lg'
   */
  spacingRight?: 'lg' | 'sm'

  /**
   * When `true`, the chevron (when {@link isInteractive} is `true`) will
   * show a skeleton.
   */
  isSkeleton?: boolean

  /**
   * How to align the content.
   *
   * @default 'top'
   */
  align?: 'top' | 'center'

  /**
   * How to vertically align the extras.
   *
   * @default 'center'
   */
  extrasAlign?: 'top' | 'center'

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Content for card items such as {@link CardButton}
 * and {@link CardListItem}.
 */
export function CardItemContent({
  children,
  extras,
  isInteractive,
  icon,
  isSkeleton,
  spacingY = 'lg',
  spacingRight = 'lg',
  align = 'top',
  extrasAlign = 'center',
  decoration,
  layout,
  testId,
}: CardItemContentProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const [{ selectionMode = 'none' }] = useContextProps(
    {} as GridListProps<unknown>,
    null,
    GridListContext,
  )

  const { isSelected, onChange } =
    useSlottedContext(CheckboxContext, 'selection') ?? {}

  return (
    <div
      data-test={testId}
      className={cx(
        styles.itemContent,
        align === 'center' && styles.alignCenter,
        extrasAlign === 'top' && styles.extrasAlignTop,
        spacingY === 'base' && styles.spacingBase,
        spacingRight === 'sm' && styles.spacingRightSm,
        layout === 'stacked' && styles.stacked,
      )}
    >
      {selectionMode !== 'none' && (
        <div className={styles.itemContentSelectionContainer}>
          {selectionMode === 'single' ? (
            <Radio
              isSkeleton={isSkeleton}
              readOnly
              variant="check"
              checked={isSelected}
            />
          ) : (
            <Checkbox
              isSkeleton={isSkeleton}
              onChange={onChange}
              checked={isSelected}
            />
          )}
        </div>
      )}
      {decoration
        ? decoration
        : icon && <CardItemContentDecoration>{icon}</CardItemContentDecoration>}

      <div className={styles.itemContentText}>{children}</div>

      <div className={styles.itemContentExtrasContainer}>
        {flattenChildren(extras).length > 0 && (
          <div className={styles.extras}>{extras}</div>
        )}

        {isInteractive && (
          <div className={styles.arrowContainer}>
            <CardItemIcon
              skeleton={isSkeleton}
              variant="secondary"
              name="chevron"
            />
          </div>
        )}
      </div>
    </div>
  )
}

interface CardItemContentDecorationProps {
  children?: ReactNode
  spacingX?: 'default' | 'base' | 'md'
  align?: 'start' | 'center'
  justify?: 'start' | 'center'
  thumbnail?: boolean
}

/**
 * Card Item Content Decoration
 */
export function CardItemContentDecoration({
  children,
  spacingX,
  align = 'start',
  justify = 'start',
  thumbnail,
}: CardItemContentDecorationProps) {
  return (
    <div
      className={cx(
        styles.itemContentDecoration,
        spacingX === 'base' && styles.spacingXBase,
        spacingX === 'md' && styles.spacingXMd,
        align === 'center' && styles.alignCenter,
        justify === 'center' && styles.justifyCenter,
        thumbnail && styles.thumbnail,
      )}
    >
      {children}
    </div>
  )
}

interface CardListEmptyStateProps {
  title: ReactNode
  action?: ReactNode
  testId?: string
}

/**
 * Card List Empty State
 */
export function CardListEmptyState({
  title,
  action,
  testId,
}: CardListEmptyStateProps) {
  return (
    <div data-test={testId} className={styles.cardListEmptyState}>
      <p className={styles.cardListEmptyStateTitle}>{title}</p>
      {action && (
        <div className={styles.cardListEmptyStateAction}>{action}</div>
      )}
    </div>
  )
}

interface CardContentDecorationProps {
  children?: ReactNode
  spacing?: '2xl' | 'md'
}

/**
 * Decoration, such as an icon or avatar, to be used at
 * the top of a card's content.
 */
export function CardContentDecoration({
  children,
  spacing = '2xl',
}: CardContentDecorationProps) {
  const spacingClassName = match(spacing, {
    '2xl': 'pb-2xl',
    md: 'pb-md',
  })
  return (
    <div
      className={cx(
        'flex items-center justify-center flex-col',
        spacingClassName,
      )}
    >
      {children}
    </div>
  )
}

interface CardTitleProps {
  children?: ReactNode
  id?: string
  overflowWrap?: 'break-all'
  testId?: string
}

/**
 * Title of the card.
 */
export function CardTitle({
  id,
  children,
  overflowWrap,
  testId,
}: CardTitleProps) {
  return (
    <p
      id={id}
      data-test={testId}
      className={cx(styles.title, overflowWrap === 'break-all' && 'break-all')}
    >
      {children}
    </p>
  )
}

interface CardSubtitleProps {
  children?: ReactNode
  variant?: 'base' | 'description' | 'sm' | 'sm-description' | 'label'
  testId?: string
}

/**
 * Subtitle for a card.
 */
export function CardSubtitle({
  children,
  variant = 'base',
  testId,
}: CardSubtitleProps) {
  return (
    <div
      data-test={testId}
      className={cx(
        styles.subtitle,
        match(variant, {
          base: styles.subtitleBase,
          description: cx(styles.subtitleBase, styles.description),
          sm: styles.subtitleSm,
          'sm-description': cx(styles.subtitleSm, styles.description),
          label: styles.label,
        }),
      )}
    >
      {children}
    </div>
  )
}

interface CardContentFooterActionsProps {
  children?: ReactNode
  isSkeleton?: boolean
  stacked?: boolean
  testId?: string
}

/**
 * Actions for the bottom part of a card's content.
 * Use `StyledLink`s or `Button`s (with `variant="text-inline"`) as children.
 */
export function CardContentFooterActions({
  children,
  isSkeleton,
  testId,
  stacked,
}: CardContentFooterActionsProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const childrenArray = flattenChildren(children)
  const count = childrenArray.length
  if (count === 0) {
    return null
  }

  return (
    <div
      data-test={testId}
      className={cx(styles.contentFooterActions, stacked && styles.stacked)}
    >
      {childrenArray.map((c, i) => (
        <Fragment key={i}>
          {c}
          {i < count - 1 ? (
            <span className={styles.cardContentFooterActionSeparator}>
              <AutoSkeleton isSkeleton={isSkeleton}>|</AutoSkeleton>
            </span>
          ) : null}
        </Fragment>
      ))}
    </div>
  )
}

interface CardFooterActionProps {
  label: string
  onPress?: () => unknown
  to?: string
  replace?: boolean
  isDisabled?: boolean
  testId?: string
}

/**
 * Action to use as the last item in the card.
 */
export function CardFooterAction({
  to,
  onPress,
  replace,
  isDisabled,
  label,
  testId,
}: CardFooterActionProps) {
  const Component = to ? RawLink : RawButton

  return (
    <Component
      to={to}
      onPress={onPress}
      replace={replace}
      isDisabled={isDisabled}
      data-test={testId}
      className={({
        isFocusVisible,
        isHovered,
        isPressed,
        isDisabled,
      }: LinkRenderProps | ButtonRenderProps) =>
        cx(
          styles.footerAction,
          isFocusVisible && styles.focusVisible,
          isHovered && styles.hover,
          isPressed && styles.pressed,
          isDisabled && styles.disabled,
        )
      }
    >
      {label}
    </Component>
  )
}

/**
 * Gets the skeleton class name to use for cards.
 *
 * @param isSkeleton
 * @param color
 */
export function getSkeletonClassName(
  isSkeleton: boolean | undefined | null,
  color: SkeletonColor | undefined | null,
): string | null {
  if (!isSkeleton) {
    return null
  }

  return cx(
    styles.skeleton,
    color === 'secondary' ? styles.skeletonSecondary : styles.skeletonPrimary,
  )
}
